/**
 * featured-collection-lazy.js
 *
 * Swaps the card strip into lazy featured-collection sections (see
 * sections/featured-collection.liquid, setting "lazy_render"). Each placeholder carries:
 *   data-fc-url        /collections/<handle>?section_id=featured-collection-lazy (locale-aware)
 *   data-fc-limit      cards this instance shows
 *   data-fc-slider-id  the slider id this instance would have rendered server-side
 *
 * Flow: IntersectionObserver (one screen of look-ahead) -> fetch the Section Rendering
 * API response -> parse it OFF-document (custom elements do not upgrade in a DOMParser
 * document, so trimming/renaming happens before anything initialises) -> rename the
 * slider id so several strips on one page never share an id -> drop cards past the
 * limit -> replace the placeholder -> let the theme upgrade images (window.initLazyImages
 * is main.js's own helper; product-recommendations calls it the same way). carousel-slider
 * and product-card are custom elements and self-initialise on insertion; the scroll-reveal
 * script watches DOM mutations.
 *
 * Failure: one retry after 2 s, then the placeholder collapses (its reserved height is
 * removed) so the page never holds a blank band. Vanilla JS, no dependencies.
 */
(function () {
  'use strict';

  // The section emits this <script> per lazy instance; the browser fetches it once but
  // runs each tag, so guard against observing (and fetching) every placeholder N times.
  if (window.__mruFcLazy) return;
  window.__mruFcLazy = true;

  var SELECTOR = '[data-fc-lazy]:not([data-fc-state])';
  var LOOKAHEAD = '0px 0px 1200px 0px';
  var LAZY_SLIDER_ID = 'slider-featured-collection-lazy';

  function collapse(placeholder) {
    placeholder.classList.remove('fc-lazy');
    placeholder.removeAttribute('aria-busy');
  }

  function inject(placeholder, html) {
    var sliderId = placeholder.getAttribute('data-fc-slider-id') || LAZY_SLIDER_ID;
    var limit = parseInt(placeholder.getAttribute('data-fc-limit'), 10);

    // Give this instance its own slider id (the fetched markup always carries the lazy section's).
    if (sliderId !== LAZY_SLIDER_ID) {
      html = html.split(LAZY_SLIDER_ID).join(sliderId);
    }

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var strip = doc.querySelector('carousel-slider') || doc.querySelector('.products-grid-container');
    if (!strip) return false;

    if (limit > 0) {
      var items = strip.querySelectorAll('ul[role="list"] > li');
      for (var i = items.length - 1; i >= limit; i--) {
        items[i].parentNode.removeChild(items[i]);
      }
    }

    // Injected cards must never depend on the scroll-reveal pipeline: under
    // .cc-animate-enabled a [data-cc-animate] element is opacity:0 until
    // animate-on-scroll.js (MutationObserver, debounced) initialises it, and a miss
    // there would leave a whole strip invisible. The strip lands fully visible; the
    // section header above it keeps its reveal.
    var reveal = strip.querySelectorAll('[data-cc-animate]');
    for (var r = 0; r < reveal.length; r++) {
      reveal[r].removeAttribute('data-cc-animate');
      reveal[r].removeAttribute('data-cc-animate-delay');
    }

    // insertAdjacentHTML (not adoptNode) so custom elements upgrade in THIS document.
    placeholder.insertAdjacentHTML('afterend', strip.outerHTML);
    placeholder.parentNode.removeChild(placeholder);

    if (typeof window.initLazyImages === 'function') {
      window.initLazyImages();
    }
    return true;
  }

  function load(placeholder, attempt) {
    var url = placeholder.getAttribute('data-fc-url');
    if (!url) { collapse(placeholder); return; }
    placeholder.setAttribute('data-fc-state', 'loading');

    fetch(url, { credentials: 'same-origin', headers: { 'Accept': 'text/html' } })
      .then(function (res) {
        if (!res.ok) throw new Error('featured-collection-lazy: HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        if (!inject(placeholder, html)) throw new Error('featured-collection-lazy: no strip in response');
      })
      .catch(function (err) {
        if (attempt < 1) {
          setTimeout(function () { load(placeholder, attempt + 1); }, 2000);
        } else {
          collapse(placeholder);
          if (window.console && console.warn) console.warn(err);
        }
      });
  }

  function init() {
    var placeholders = document.querySelectorAll(SELECTOR);
    if (!placeholders.length) return;

    if (!('IntersectionObserver' in window)) {
      placeholders.forEach(function (p) { load(p, 0); });
      return;
    }

    var io = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        load(entry.target, 0);
      });
    }, { rootMargin: LOOKAHEAD });

    placeholders.forEach(function (p) { io.observe(p); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
